import { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Typography,
  Box,
  Container,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import {
  ORG_FETCH_PARTICULAR_REVIEW,
  ORG_SEND_REPORT,
  ORG_UPDATERATING,
} from "../../api/org";
import dayjs from "dayjs";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import PDFDocument from "./StudentReportPDF"; // adjust path if needed
import { pdf } from "@react-pdf/renderer";

const OrgBookings = () => {
  const { id } = useParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [formData, setFormData] = useState({});
  const [screenshot, setScreenshot] = useState(null);
  const [file, setFile] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const location = useLocation();
  const orgDetails = location?.state?.org || {};
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();

  // Fetch bookings on component mount
  useEffect(() => {
    axios
      .get(ORG_FETCH_PARTICULAR_REVIEW + id)
      .then((response) => {
        const sortedBookings = response.data.data.sort((a, b) => {
          const dateTimeA = dayjs(
            `${a.bookingDate} ${a.time}`,
            "YYYY-MM-DD h:mm A"
          );
          const dateTimeB = dayjs(
            `${b.bookingDate} ${b.time}`,
            "YYYY-MM-DD h:mm A"
          );
          return dateTimeA.diff(dateTimeB);
        });
        setBookings(sortedBookings);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching bookings:", error);
        setLoading(false);
      });
    setRefresh(false);
  }, [id, refresh]);

  // Handle dialog open
  const handleDialogOpen = (booking) => {
    setCurrentBooking(booking);

    // Directly set the screenshot from the booking object
    setScreenshot(booking.screenshot || null);

    // Set formData for the current booking
    setFormData((prevData) => ({
      ...prevData,
      [booking._id]: booking.formData || {}, // Assuming formData is stored within booking
    }));

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentBooking(null);
    setFile(null);
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => {
      const prevBookingData = prev[currentBooking._id] || {};

      return {
        ...prev,
        [currentBooking._id]: {
          ...prevBookingData,
          [name]: value,
        },
      };
    });
  };

  const handleScreenshotUpload = (file) => {
    const reader = new FileReader();
    setFile(file);
    reader.onload = () => {
      setScreenshot(reader.result);
    };
    reader.readAsDataURL(file);
    handleInputChange("screenshot", file);
  };

  const handlePasteScreenshot = (e) => {
    const clipboardData = e.clipboardData;
    const file = clipboardData.files[0];
    if (file && file.type.startsWith("image/")) {
      handleScreenshotUpload(file);
    }
  };

  const handleSubmit = () => {
    const dataToSubmit = formData[currentBooking._id] || {};
    const formDataToSend = new FormData();

    for (const [key, value] of Object.entries(dataToSubmit)) {
      formDataToSend.append(key, value);
    }

    // if (screenshot) {
    //   formDataToSend.append("screenshot", file);
    // }

    axios
      .put(
        `${ORG_UPDATERATING}${currentBooking._id}/rating/final`,
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      )
      .then((response) => {
        setBookings((prev) =>
          prev.map((booking) =>
            booking._id === currentBooking._id
              ? { ...booking, ...response.data }
              : booking
          )
        );
        handleDialogClose();
      })
      .catch((error) => {
        console.error("Error submitting form data:", error);
      });
  };

  const columns = [
    { field: "name", headerName: "Name", width: 150 },
    { field: "email", headerName: "Email", width: 150 },
    { field: "whatsappNumber", headerName: "WhatsApp", width: 100 },
    { field: "studentCode", headerName: "Student Code", width: 80 },
    {
      field: "bookingDate",
      headerName: "Booking Date",
      width: 150,
      valueGetter: (params) =>
        params.row.bookingDate
          ? dayjs(params.row.bookingDate).format("DD MMM YYYY")
          : "Not Booked",
    },
    { field: "time", headerName: "Booking Time", width: 100 },
    {
      field: "form",
      headerName: "Form",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleDialogOpen(params.row)}
          sx={{
            background: params.row.ratingOverall && "orange",
          }}
        >
          {params.row.ratingOverall ? "Edit Review" : "Add Review"}
        </Button>
      ),
    },
    {
      field: "sendEmail",
      headerName: "Send Email",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleSendEmail(params.row)}
          disabled={params.row.status !== "sent"}
        >
          Send Email
        </Button>
      ),
    },

    {
      field: "report",
      headerName: "Report",
      renderCell: (params) =>
        params?.row?.reportUrl ? "Generated" : "Not Generated",
    },
  ];

  const handleSendEmail = async (bookingData) => {
    try {
      const response = await fetch(ORG_SEND_REPORT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingData),
      });

      if (response.ok) {
        setRefresh(true);
        alert("Email sent successfully!");
      } else {
        alert("Failed to send email.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("An error occurred while sending the email.");
    }
  };

  // Function to download and zip generated reports
  const downloadGeneratedReports = async () => {
    try {
      setDownloadLoading(true);
      const generatedReports = bookings.filter((booking) => booking.reportUrl);

      if (generatedReports.length === 0) {
        alert("No generated reports found.");
        setDownloadLoading(false);
        return;
      }

      const zip = new JSZip();
      const reportFolder = zip.folder("generated-reports");

      // Create an array of promises for fetching each report
      const downloadPromises = generatedReports.map(async (booking) => {
        try {
          const response = await fetch(booking.reportUrl);
          if (!response.ok)
            throw new Error(`Failed to fetch ${booking.reportUrl}`);

          const blob = await response.blob();
          const fileName = `${booking.studentCode || booking.name}_${
            booking._id
          }.pdf`;
          reportFolder.file(fileName, blob);

          return { success: true, id: booking._id };
        } catch (error) {
          console.error(`Error downloading report for ${booking._id}:`, error);
          return { success: false, id: booking._id, error };
        }
      });

      // Wait for all downloads to complete
      await Promise.all(downloadPromises);

      // Generate the zip file
      const zipContent = await zip.generateAsync({ type: "blob" });

      // Save the zip file
      const orgName = orgDetails.name || "organization";
      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      saveAs(zipContent, `${orgName}_generated_reports_${timestamp}.zip`);

      setDownloadLoading(false);
    } catch (error) {
      console.error("Error creating zip file:", error);
      alert("Failed to create zip file. Please try again.");
      setDownloadLoading(false);
    }
  };

  // Function to download and zip non-generated reports (bookings without reportUrl)
  const generateAndDownloadPDFs = async () => {
    try {
      setDownloadLoading(true);
      const zip = new JSZip();
      const reportFolder = zip.folder("generated-reports");

      const relevantBookings = bookings.filter((booking) => {
        // Modify filter condition as needed
        return booking.ratingOverall; // only generate PDFs for reviewed bookings
      });
      console.log(relevantBookings);
      for (const booking of relevantBookings) {
        const blob = await pdf(
          <PDFDocument
            student={booking}
            labels={[
              {
                label: "Communication",
                value: booking.communicationRating,
                average: 6,
              },
              {
                label: "Confidence",
                value: booking.confidenceRating,
                average: 6,
              },
              {
                label: "Knowledge",
                value: booking.knowledgeRating,
                average: 6,
              },
              {
                label: "Flexibility & Agility",
                value: booking.flexibilityAgilityRating,
                average: 6,
              },
              {
                label: "Experience",
                value: booking.experienceRating,
                average: 6,
              },
              { label: "Training", value: booking.trainingRating, average: 6 },
              {
                label: "Analytical Thinking",
                value: booking.analyticalThinkingRating,
                average: 6,
              },
              {
                label: "Creative Thinking",
                value: booking.creativeThinkingRating,
                average: 6,
              },
              { label: "Dynamism", value: booking.dynamismRating, average: 6 },
              {
                label: "Technical Skillset",
                value: booking.technicalSkillsetRating,
                average: 6,
              },
            ]}
          />
        ).toBlob();
        const fileName = `${booking.studentCode || booking.name}_${
          booking._id
        }.pdf`;
        reportFolder.file(fileName, blob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      const orgName = orgDetails.name || "organization";
      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      saveAs(content, `${orgName}_pdf_reports_${timestamp}.zip`);

      setDownloadLoading(false);
    } catch (error) {
      console.error("Error generating PDFs:", error);
      alert("An error occurred while generating PDFs.");
      setDownloadLoading(false);
    }
  };

  return (
    <Container sx={{ width: "100%" }} onPaste={handlePasteScreenshot}>
      <Box sx={{ mt: 4, width: "100%" }}>
        <Typography variant="h4" gutterBottom>
          Organization Bookings
        </Typography>

        <Box sx={{ mb: 3 }}>
          {/* <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Organization Email:</strong> {orgDetails.email || 'N/A'}
          </Typography> */}
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Organization ID:</strong> {orgDetails.orgCode || "N/A"}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Organization Name:</strong> {orgDetails.name || "N/A"}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Email:</strong> {orgDetails.email || "N/A"}
          </Typography>
        </Box>

        {/* Download Reports Buttons */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={downloadGeneratedReports}
            disabled={downloadLoading || !bookings.some((b) => b.reportUrl)}
          >
            {downloadLoading
              ? "Processing..."
              : "Download Generated Reports (ZIP)"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={generateAndDownloadPDFs}
            // disabled={downloadLoading || !bookings.some((b) => !b.reportUrl)}
          >
            {downloadLoading ? "Processing..." : "Download All Reports (ZIP)"}
          </Button>
        </Box>

        <Box sx={{ height: "90vh", width: "100%" }}>
          <DataGrid
            rows={bookings}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            getRowId={(row) => row._id}
            loading={loading}
          />
        </Box>
      </Box>

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>
          {currentBooking?.ratingOverall ? "Edit Review" : "Add Review"}
        </DialogTitle>
        <DialogContent>
          {[
            { name: "communicationRating", label: "Communication Rating" },
            { name: "knowledgeRating", label: "Knowledge Rating" },
            { name: "experienceRating", label: "Experience Rating" },
            {
              name: "flexibilityAgilityRating",
              label:
                "Flexibility & Agility, Curiosity & Lifelong Learning Attitude",
            },
            {
              name: "analyticalThinkingRating",
              label: "Analytical Thinking",
            },
            {
              name: "creativeThinkingRating",
              label: "Creative Thinking / Out of the Box Thinking",
            },
            { name: "ratingOverall", label: "Rating Overall" },
          ].map((ratingField) => (
            <Box key={ratingField.name} mt={2}>
              <FormControl component="fieldset">
                <FormLabel>{ratingField.label}</FormLabel>
                <RadioGroup
                  row
                  value={
                    formData[currentBooking?._id]?.[ratingField.name] ||
                    currentBooking?.[ratingField.name] ||
                    ""
                  }
                  onChange={(e) =>
                    handleInputChange(ratingField.name, e.target.value)
                  }
                >
                  {[...Array(11).keys()].map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value.toString()}
                      control={<Radio />}
                      label={value}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          ))}

          <Box mt={3}>
            <Typography>Feedback:</Typography>
            <ReactQuill
              value={
                formData[currentBooking?._id]?.feedback ||
                currentBooking?.feedback ||
                ""
              }
              onChange={(value) => handleInputChange("feedback", value)}
              placeholder="Write your feedback..."
            />
          </Box>
          <Box mt={3}>
            <Typography>Strength:</Typography>
            <ReactQuill
              value={
                formData[currentBooking?._id]?.strength ||
                currentBooking?.strength ||
                ""
              }
              onChange={(value) => handleInputChange("strength", value)}
              placeholder="Write the student's strengths..."
            />
          </Box>
          <Box mt={3}>
            <Typography>Weakness:</Typography>
            <ReactQuill
              value={
                formData[currentBooking?._id]?.weakness ||
                currentBooking?.weakness ||
                ""
              }
              onChange={(value) => handleInputChange("weakness", value)}
              placeholder="Write the student's weaknesses..."
            />
          </Box>

          <Box
            sx={{
              mt: 2,
              p: 2,
              border: "2px dashed #ccc",
              borderRadius: "8px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
            }}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file) handleScreenshotUpload(file);
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            {screenshot ? (
              <img
                src={screenshot || "/placeholder.svg"}
                alt="Screenshot Preview"
                style={{ maxWidth: "100%", maxHeight: 200 }}
              />
            ) : (
              <Typography>
                Paste, Drag & Drop, or Click to Upload Screenshot
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            component="label"
            fullWidth
            sx={{ mt: 2 }}
          >
            Upload Screenshot
            <input
              type="file"
              hidden
              onChange={(e) => handleScreenshotUpload(e.target.files[0])}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrgBookings;
